import React, { useEffect, useState, useMemo, useRef } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { useFetch } from '../hooks/useFetch';
import { useAuth } from '../AuthContext';
import { useTranslation } from 'react-i18next';
import Header from "./common/Header";
import Sidebar from "./common/Sidebar";
import './Forecast.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend
);

// Función para calcular la trendline (regresión lineal simple)
function calculateTrendline(data) {
  const n = data.length;
  if (n === 0) return [];

  const sumX = data.reduce((acc, _, i) => acc + i, 0);
  const sumY = data.reduce((acc, d) => acc + d.total, 0);
  const sumXY = data.reduce((acc, d, i) => acc + i * d.total, 0);
  const sumX2 = data.reduce((acc, _, i) => acc + i * i, 0);

  const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
  const intercept = (sumY - slope * sumX) / n;

  return data.map((d, i) => intercept + slope * i);
}

const Forecast = () => {
  const { user } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [forecast, setForecast] = useState([]);

  // Cached fetch via useFetch (same caching logic used elsewhere)
  const [expensesApiData] = useFetch('/transactions?limit=500');

  useEffect(() => {
    // Unwrap common shapes: axios-like, our API envelope, or plain list
    const list =
      expensesApiData?.data?.data?.transactions ??
      expensesApiData?.data?.transactions ??
      expensesApiData?.transactions ??
      [];
    if (Array.isArray(list) && list.length) {
      setExpenses(list);
    }
  }, [expensesApiData]);

  // Forecast paging state
  const [forecastPage, setForecastPage] = useState(0);
  const [visibleRows, setVisibleRows] = useState(12); // will adapt to combined charts height
  const pageSize = Math.max(1, visibleRows);
  const pagedForecast = forecast.slice(
    forecastPage * pageSize,
    (forecastPage + 1) * pageSize
  );

  const { t, i18n } = useTranslation();
  // Locale derived from current i18n language
  const locale = i18n?.language === 'en' ? 'en-US'
                : i18n?.language === 'es' ? 'es-CL'
                : (i18n?.language || 'es-CL');

  const chartData = useMemo(() => {
    if (typeof expenses === 'undefined') {
      return {};
    }
    // Calculate totalsByMonth first
    const totalsByMonth = {};
    expenses.forEach((e) => {
      const d = new Date(e.date);
      const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      totalsByMonth[monthKey] = (totalsByMonth[monthKey] || 0) + e.amount;
    });
    // Use all distinct months present in expenses
    const allMonths = Array.from(new Set(expenses.map(e => {
      const d = new Date(e.date);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    }))).sort();

    const labels = allMonths.map((monthKey) => {
      const [year, month] = monthKey.split('-');
      return new Date(year, Number(month) - 1).toLocaleString(locale, {
        month: 'short',
        year: 'numeric',
      });
    });

    const totals = allMonths.map((monthKey) => totalsByMonth[monthKey] || 0);

    const trendline = calculateTrendline(allMonths.map((m, i) => ({ total: totals[i] })));

    // Add projection for the next 3 months based on average of totals or Forecast table totals
    const avgMonthly =
      totals.length > 0 ? totals.reduce((a, b) => a + b, 0) / totals.length : 0;
    const lastDate = new Date(allMonths[allMonths.length - 1] + '-01');
    // Next 3 months labels
    const projectedMonths = [];
    for (let i = 1; i <= 3; i++) {
      const next = new Date(lastDate.getFullYear(), lastDate.getMonth() + i, 1);
      projectedMonths.push(
        next.toLocaleString(locale, { month: 'short', year: 'numeric' })
      );
    }
    // Use Forecast totals (projected + installments) if already computed; fallback to avgMonthly
    const projectedTotals = (forecast && forecast.length)
      ? forecast.slice(0, 3).map(f => Number(f.total) || 0)
      : Array(3).fill(avgMonthly);

    return {
      labels: [...labels, ...projectedMonths],
      datasets: [
        {
          label: t('forecast.totalSpent', 'Total Gastado'),
          data: [...totals, ...Array(3).fill(null)],
          borderColor: 'rgba(54, 162, 235, 1)',
          backgroundColor: 'rgba(54, 162, 235, 0.2)',
          tension: 0.3,
          fill: false,
        },
        {
          label: t('forecast.projected', 'Proyección'),
          data: [
            ...Array(Math.max(0, totals.length - 1)).fill(null),
            totals.length > 0 ? totals[totals.length - 1] : null,
            ...projectedTotals
          ],
          borderColor: 'rgba(255, 206, 86, 1)',
          backgroundColor: 'rgba(255, 206, 86, 0.3)',
          borderDash: [4, 4],
          tension: 0.3,
          fill: false,
          spanGaps: true,
        },
        {
          label: t('forecast.trendline', 'Trendline'),
          data: trendline,
          borderColor: 'rgba(255, 99, 132, 1)',
          borderDash: [5, 5],
          fill: false,
          pointRadius: 0,
          hoverRadius: 0,
          tension: 0,
        },
      ],
    };
  }, [expenses, forecast, t, locale]);

  // CATEGORY CHART STATE AND MEMOIZATION
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Refs for syncing chart/table heights
  const chartHeightRef = useRef(null);      // top total-by-month chart
  const categoryChartRef = useRef(null);    // bottom category chart
  const tableHeightRef = useRef(null);      // right column table

  const categories = useMemo(() => {
    const catSet = new Set();
    if (typeof expenses === 'undefined') {
      return Array.from(catSet).sort();
    }
    expenses.forEach(e => {
      const catName = e.data?.subcategory?.category?.name;
      if (catName) catSet.add(catName);
    });
    return Array.from(catSet).sort();
  }, [expenses]);

  useEffect(() => {
    if (categories.length > 0 && !selectedCategory) {
      setSelectedCategory(categories[0]);
    }
  }, [categories, selectedCategory]);

  const chartDataCategory = useMemo(() => {
    if (!selectedCategory) return null;

    // Calculate totalsByMonth for the selected category first
    const totalsByMonth = {};
    expenses.forEach(e => {
      const catName = e.data?.subcategory?.category?.name;
      if (catName === selectedCategory) {
        const d = new Date(e.date);
        const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        totalsByMonth[monthKey] = (totalsByMonth[monthKey] || 0) + e.amount;
      }
    });
    // Use all distinct months present in expenses
    const allMonths = Array.from(new Set(expenses.map(e => {
      const d = new Date(e.date);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    }))).sort();

    const labels = allMonths.map(monthKey => {
      const [year, month] = monthKey.split('-');
      return new Date(year, Number(month) - 1).toLocaleString(locale, {
        month: 'short',
        year: 'numeric',
      });
    });

    const totals = allMonths.map(monthKey => totalsByMonth[monthKey] || 0);
    const trendline = calculateTrendline(allMonths.map((m, i) => ({ total: totals[i] })));

    return {
      labels,
      datasets: [
        {
          label: `${t('forecast.totalSpent', 'Total Gastado')} - ${selectedCategory}`,
          data: totals,
          borderColor: 'rgba(75, 192, 192, 1)',
          backgroundColor: 'rgba(75, 192, 192, 0.2)',
          tension: 0.3,
          fill: false,
        },
        {
          label: t('forecast.trendline', 'Trendline'),
          data: trendline,
          borderColor: 'rgba(255, 159, 64, 1)',
          borderDash: [5, 5],
          fill: false,
          pointRadius: 0,
          hoverRadius: 0,
          tension: 0,
        },
      ],
    };
  }, [expenses, selectedCategory, t, locale]);

  // Keep table height in sync with chart container height
  useEffect(() => {
    const syncHeights = () => {
      const topChartEl = chartHeightRef.current;
      const catChartEl = categoryChartRef.current;
      const tableEl = tableHeightRef.current;
      if (!tableEl) return;

      const chartsCol = document.querySelector('.forecast-chart-column');
      const tableCol  = tableEl.closest('.forecast-left-column');

      // If we cannot measure columns, bail safely
      if (!chartsCol || !tableCol) {
        tableEl.style.maxHeight = '';
        tableEl.style.overflowY = 'visible';
        tableEl.style.minHeight = '0';
        return;
      }

      const chartsRect = chartsCol.getBoundingClientRect();
      const tableRect  = tableCol.getBoundingClientRect();

      // Detect wrapped layout: table column is visually below the charts column
      const isWrapped = tableRect.top >= chartsRect.bottom - 1;

      // Helper to compute how many rows fit given an available pixel height
      const computeRowsFit = (availablePx) => {
        const theadTr = tableEl.querySelector('thead tr');
        const tbodyTr = tableEl.querySelector('tbody tr');
        const headerH = theadTr ? theadTr.getBoundingClientRect().height : 40;
        const rowH    = tbodyTr ? tbodyTr.getBoundingClientRect().height : 36;
        const padding = 12; // small breathing room
        return Math.max(1, Math.floor((availablePx - headerH - padding) / rowH));
      };

      if (isWrapped) {
        // Stacked layout: let the table take full width below charts.
        // Use viewport to decide a sensible number of rows without forcing a max-height.
        const viewportAvail = Math.max(180, window.innerHeight - tableEl.getBoundingClientRect().top - 24);
        const rowsFit = computeRowsFit(viewportAvail);
        setVisibleRows(prev => {
          const target = Math.min(forecast.length, rowsFit);
          return target > 0 ? target : prev;
        });

        // Do not constrain height; avoid scroll inside to reduce jumpiness in stacked layout
        tableEl.style.maxHeight = '';
        tableEl.style.overflowY = 'visible';
        tableEl.style.minHeight = '0';
        tableEl.style.height = '';
        return; // done for wrapped case
      }

      // Side‑by‑side layout: cap table height to combined charts height (top + category)
      let chartH = 0;
      if (topChartEl) chartH += topChartEl.getBoundingClientRect().height || 0;
      if (catChartEl) {
        const h2 = catChartEl.getBoundingClientRect().height || 0;
        const mt = parseFloat(window.getComputedStyle(catChartEl).marginTop || '0') || 0;
        chartH += mt + h2;
      }
      if (!chartH) {
        // Fallback: remove constraints
        tableEl.style.maxHeight = '';
        tableEl.style.overflowY = 'visible';
        tableEl.style.minHeight = '0';
        return;
      }

      // Space used above the table inside its column (title + pager)
      const topOffset = tableEl.offsetTop - tableCol.offsetTop;
      // Make the table a bit longer: aim for combined charts height + 120px (but never exceed available)
      const desired = chartH - topOffset + 120; // grow a bit more (~120px)
      const available = chartsRect.height - topOffset - 8; // safe cap within charts column
      let maxH = Math.max(120, Math.min(desired, available));

      // Fix exact height so the table visually matches the combined charts
      tableEl.style.height = maxH + 'px';
      tableEl.style.maxHeight = maxH + 'px';
      tableEl.style.overflowY = 'auto';
      tableEl.style.minHeight = '0';

      const rowsFit = computeRowsFit(maxH);
      setVisibleRows(prev => {
        const target = Math.min(forecast.length, rowsFit);
        return target > 0 ? target : prev;
      });
    };
    // initial and on resize
    syncHeights();
    window.addEventListener('resize', syncHeights);
    return () => window.removeEventListener('resize', syncHeights);
  }, [chartData, chartDataCategory, forecast.length]);

  // Reset page when page size changes
  useEffect(() => {
    setForecastPage(0);
  }, [pageSize]);

  const options = {
    responsive: true,
    animation: { duration: 600, easing: 'easeOutQuart' },
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top' },
      tooltip: {
        callbacks: {
          label: (context) =>
            context.parsed.y.toLocaleString(locale, { style: 'currency', currency: 'CLP' }),
        },
      },
    },
    scales: {
      y: {
        ticks: {
          callback: (value) =>
            value.toLocaleString(locale, { style: 'currency', currency: 'CLP' }),
        },
      },
    },
  };

  // DEBUG: Log distinct months in expenses
  useEffect(() => {
    if (expenses && expenses.length > 0) {
      console.log('Distinct months in expenses:', [...new Set(expenses.map(e => new Date(e.date).toISOString().slice(0, 7)))]);
    }
  }, [expenses]);

  useEffect(() => {
    if (!expenses || expenses.length === 0) return;

    // Find the latest expense date to base the forecast from
    const latestExpenseDate = new Date(Math.max(...expenses.map(e => new Date(e.date))));
    const baseYear = latestExpenseDate.getFullYear();
    const baseMonth = latestExpenseDate.getMonth(); // 0-based


    // Estimate future monthly expenses based on average of total monthly expenses
    const totalsByMonth = {};
    expenses.forEach(e => {
      const d = new Date(e.date);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      totalsByMonth[key] = (totalsByMonth[key] || 0) + e.amount;
    });

    const pastTotals = Object.values(totalsByMonth);
    // --- Improved forecast with seasonality ---
    // Compute average per month across all years to detect seasonal patterns
    const monthAverages = {};
    expenses.forEach(e => {
      const d = new Date(e.date);
      const key = d.getMonth(); // 0-11
      if (!monthAverages[key]) monthAverages[key] = [];
      monthAverages[key].push(e.amount);
    });
    const avgByMonth = Object.fromEntries(
      Object.entries(monthAverages).map(([m, arr]) => [Number(m), arr.reduce((a,b)=>a+b,0)/arr.length])
    );

    // Weight certain months according to southern hemisphere spending patterns
    // - Enero–Marzo: vacation (higher)
    // - Marzo–Diciembre: school period (moderate)
    // - Nov–Dic: high due to Christmas
    // - Julio: mid-year break (slight increase)
    const seasonWeights = [
      1.2, // Jan
      1.15, // Feb
      1.1, // Mar
      1.05, // Apr
      1.05, // May
      1.1, // Jun
      1.15, // Jul
      1.05, // Aug
      1.05, // Sep
      1.05, // Oct
      1.2, // Nov
      1.35  // Dec (Christmas season)
    ];

    // Base average from all past totals (for scaling)
    const globalAvg = pastTotals.length > 0
      ? pastTotals.reduce((a,b)=>a+b,0)/pastTotals.length
      : 0;

    // Function to estimate spending for a specific month index (0–11)
    const seasonalEstimate = (monthIdx) => {
      const base = avgByMonth[monthIdx] || globalAvg;
      const weight = seasonWeights[monthIdx] || 1.0;
      return base * weight;
    };

    // Gather installment amounts per future month (robust: prefer structured fields, no double division)
    const remaining = [];
    expenses.forEach(e => {
      if (!e) return;
      const d = e.data || {};

      // Prefer structured fields
      let n = Number.isFinite(d.installment_number) ? Number(d.installment_number) : null;
      let total = Number.isFinite(d.installment_total) ? Number(d.installment_total) : null;
      let perInst = null;
      if (Number.isFinite(e.installment_value)) perInst = Number(e.installment_value);
      else if (Number.isFinite(d.installment_value)) perInst = Number(d.installment_value);

      // Fallback: parse from string like "4/12"
      if (total == null || !Number.isFinite(total) || n == null || !Number.isFinite(n)) {
        const raw = e.installment ?? d.installment ?? '';
        if (typeof raw === 'string' && raw.includes('/')) {
          const [a, b] = raw.split('/').map(s => Number(String(s).replace(/[^0-9]/g, '')));
          if (!Number.isFinite(n) && Number.isFinite(a)) n = a;
          if (!Number.isFinite(total) && Number.isFinite(b)) total = b;
        }
      }

      // Fallback for per‑installment amount: many statements have amount as the installment amount
      if (!Number.isFinite(perInst)) {
        const amt = Number(e.payment ?? e.amount);
        if (Number.isFinite(amt)) perInst = amt;
      }

      if (!Number.isFinite(total) || total <= 1) return; // ignore non‑installments or 1/1
      if (!Number.isFinite(n)) n = 0;
      if (!Number.isFinite(perInst) || perInst <= 0) return;

      // Generate future months from the current installment date
      const startDate = new Date(e.date);
      for (let i = n + 1; i <= total; i++) {
        const instDate = new Date(startDate.getFullYear(), startDate.getMonth() + (i - n), 1);
        const key = `${instDate.getFullYear()}-${String(instDate.getMonth() + 1).padStart(2, '0')}`;
        remaining.push({
          key,
          amount: perInst, // use per‑installment value directly (no division)
          originalExpense: e,
          installmentNumber: i,
          totalInstallments: total,
        });
      }
    });

    // Determine how many future months to show:
    // - If there are active/pending installments, show up to the last installment month.
    // - If there are none, show only the next 3 months.
    const nextMonthDate = new Date(baseYear, baseMonth + 1, 1);
    let forecastMonthsToShow = 3; // default when no installments remain
    if (remaining.length > 0) {
      // Find the furthest future month (YYYY-MM max) in remaining installments
      const lastKey = remaining.reduce((max, r) => (r.key > max ? r.key : max), '0000-00');
      const [ly, lm] = lastKey.split('-').map(Number);
      const lastDate = new Date(ly, (lm || 1) - 1, 1);
      // Inclusive month difference between nextMonthDate and lastDate
      const monthsDiff = (lastDate.getFullYear() - nextMonthDate.getFullYear()) * 12 + (lastDate.getMonth() - nextMonthDate.getMonth());
      forecastMonthsToShow = Math.max(1, monthsDiff + 1);
    }

    const forecastData = Array.from({ length: forecastMonthsToShow }).map((_, i) => {
      const forecastDate = new Date(baseYear, baseMonth + i + 1, 1);
      const monthLabel = forecastDate.toLocaleString('es-CL', { month: 'long', year: 'numeric' });
      const projected = seasonalEstimate(forecastDate.getMonth());
      const key = `${forecastDate.getFullYear()}-${String(forecastDate.getMonth() + 1).padStart(2, '0')}`;

      // Only count installments due in this specific month, by key
      const monthlyInstallments = remaining.filter(inst => inst.key === key);
      const installments = monthlyInstallments.reduce((sum, inst) => sum + inst.amount, 0);
      // Count only installments due in this month
      const installmentCount = monthlyInstallments.length;

      return {
        month: monthLabel,
        projected,
        installments,
        installmentCount,
        total: projected + installments,
      };
    });

    setForecast(forecastData);
  }, [expenses]);

  // El usuario de Supabase no tiene `username` (está en user_metadata): esperarlo dejaba
  // la pantalla en "Cargando..." para siempre.
  if (!user) {
    return (
      <div className="dashboard-container">
        <Header title={t('sidebar.forecast', 'Forecast')} />
        <div className="dashboard-four-columns">
          <Sidebar />
          <div style={{ padding: '1rem' }}>
            {t('common.loading', 'Cargando...')}
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="dashboard-container">
      <Header title={t('sidebar.forecast', 'Forecast')} />
      <div className="dashboard-four-columns">
        <Sidebar />
        <div className="dashboard-content-two-columns">
          <div className="forecast-chart-column">
            <h2 className="dashboard-section-title" style={{ fontSize: '1.2rem' }}>
              {t('forecast.totalSpentByMonth', 'Monto Total Gastado por Mes')}
            </h2>
            <div className="forecast-chart-container" ref={chartHeightRef}>
              <Line data={chartData} options={options} />
            </div>
            <div style={{ marginTop: '2rem' }}>
              <label>
                {t('forecast.selectCategory', 'Seleccionar categoría:')}{' '}
                <select
                  value={selectedCategory || ''}
                  onChange={e => setSelectedCategory(e.target.value || null)}
                >
                  <option value="">{t('forecast.none', '-- Ninguna --')}</option>
                  {categories.map(cat => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </label>

              {chartDataCategory && (
                <div
                  className="forecast-chart-container"
                  style={{ marginTop: '1rem' }}
                  ref={categoryChartRef}
                >
                  <Line data={chartDataCategory} options={options} />
                </div>
              )}
            </div>
          </div>
          <div className="forecast-left-column">
            <h2 className="dashboard-section-title" style={{ fontSize: '1.2rem' }}>
              {t('forecast.monthlyProjection', 'Proyección Mensual de Gastos')}
            </h2>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <button onClick={() => setForecastPage(p => Math.max(p - 1, 0))} disabled={forecastPage === 0}>
                ◀
              </button>
              <span>
                {t('forecast.showingMonths', 'Mostrando meses')} {forecastPage * pageSize + 1} {t('forecast.to', 'a')}{' '}
                {Math.min((forecastPage + 1) * pageSize, forecast.length)} {t('forecast.of', 'de')} {forecast.length}
              </span>
              <button
                onClick={() =>
                  setForecastPage(p =>
                    p + 1 < Math.ceil(forecast.length / pageSize) ? p + 1 : p
                  )
                }
                disabled={(forecastPage + 1) * pageSize >= forecast.length}
              >
                ▶
              </button>
            </div>
            <div className="forecast-table-container equal-height" ref={tableHeightRef}>
              <table className="forecast-table">
              <thead>
                <tr>
                  <th>{t('forecast.month', 'Mes')}</th>
                  <th>{t('forecast.estimatedExpenses', 'Estimado Gastos')}</th>
                  <th>{t('forecast.installmentsTotal', 'Total Cuotas')}</th>
                  <th>{t('forecast.installmentsCount', 'Nro Cuotas')}</th>
                  <th>{t('forecast.total', 'Total')}</th>
                </tr>
              </thead>
              <tbody>
                {pagedForecast.map(({ month, projected, installments, installmentCount, total }) => (
                  <tr key={month}>
                    <td>{month}</td>
                    <td>{(projected ?? 0).toLocaleString(locale, { style: 'currency', currency: 'CLP' })}</td>
                    <td>
                      {(installments ?? 0).toLocaleString(locale, { style: 'currency', currency: 'CLP' })}
                    </td>
                    <td>
                      {installmentCount > 0 ? `${installmentCount}` : ''}
                    </td>
                    <td>{(total ?? 0).toLocaleString(locale, { style: 'currency', currency: 'CLP' })}</td>
                  </tr>
                ))}
              </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Forecast;